import { ArrowDropDown, ArrowDropUp, Clear } from '@mui/icons-material';
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { SxProps } from '@mui/system';
import clsx from 'clsx';
import { findIndex, findLastIndex } from 'lodash';
import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AutocompleteItem, AutocompleteList } from '../AutocompleteList';
import { useDebounce } from '../utils';
import { useStyles } from './styles';

interface AutocompleteSelectProps {
  className?: string;
  disabled?: boolean;
  fetchList?: (search: string) => Promise<AutocompleteItem[] | undefined>;
  label: string;
  mandatoryInput?: boolean;
  onChange?: (value?: AutocompleteItem) => void;
  value?: AutocompleteItem;
  variant?: 'standard' | 'filled' | 'outlined';
  withKeyBoardControl?: boolean;
  TextFieldProps?: TextFieldProps;
  startingIcon?: JSX.Element;
  sx?: SxProps;
}

export const AutocompleteSelect: FC<AutocompleteSelectProps> = ({
  className,
  disabled,
  fetchList,
  label,
  mandatoryInput,
  onChange,
  value,
  variant = 'standard',
  TextFieldProps: textFieldProps,
  startingIcon,
  sx,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [error, setError] = useState<Error | undefined>(undefined);
  const [focus, setFocus] = useState(false);
  const [list, setList] = useState<AutocompleteItem[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [scrollToItem, setScrollToItem] = useState<number>(-1);
  const [textInput, setTextInput] = useState('');

  const classes = useStyles();
  const debouncedInput = useDebounce(textInput, 500);

  const canDisplayList =
    focus && !disabled && !!(!mandatoryInput || debouncedInput);

  const handleChange = useCallback(
    (event?: ChangeEvent<{ value: string }>) => {
      const { value: inputValue = '' } = event?.target || {};
      setTextInput(inputValue);

      !focus && setFocus(true);
    },
    [focus],
  );

  const handleReset = useCallback(() => {
    handleChange();
    onChange?.(undefined);
    setFocus(false);
  }, [handleChange, onChange]);

  const handleSelect = useCallback(
    (
      selectedItem?: AutocompleteItem,
      listToCheck: AutocompleteItem[] | undefined = list,
      skipOnChange?: boolean,
    ) => {
      if (listToCheck?.length) {
        const newList = listToCheck.map((item) => ({
          ...item,
          selected: selectedItem?.id === item.id,
        }));
        const newSelectedItem = selectedItem
          ? { ...selectedItem, selected: true }
          : undefined;

        setList(newList);

        !skipOnChange && onChange?.(newSelectedItem);
      }
    },
    [list, onChange],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (list?.length) {
        const indexFirstItem = 0;
        const indexSelected = list.findIndex((item) => item.selected);
        const hasIndexSelected = indexSelected !== -1;
        const candidateNextIndex = hasIndexSelected
          ? indexSelected
          : indexFirstItem;

        switch (event.key) {
          case 'ArrowDown': {
            const indexToReplace = findIndex(
              list,
              (item, index) =>
                (!hasIndexSelected || index !== candidateNextIndex) &&
                !item.disabled,
              candidateNextIndex,
            );

            if (!focus && indexToReplace !== -1) {
              setFocus(true);
              break;
            }

            const isLastItem = indexSelected === list.length - 1;
            const newList: AutocompleteItem[] = list.map((item, i) => ({
              ...item,
              selected: (isLastItem && !i) || indexToReplace === i,
            }));

            setList(newList);
            setScrollToItem(isLastItem ? indexFirstItem : indexToReplace);
            break;
          }

          case 'ArrowUp': {
            const indexToReplace = findLastIndex(
              list,
              (item, index) => index !== candidateNextIndex && !item.disabled,
              candidateNextIndex,
            );

            if (!focus && indexToReplace !== -1) {
              setFocus(true);
              break;
            }

            const indexLastItem = list.length - 1;
            const isFirstItem = indexToReplace === -1;
            const newList: AutocompleteItem[] = list.map((item, i) => ({
              ...item,
              selected:
                (isFirstItem && indexLastItem === i) || indexToReplace === i,
            }));

            setList(newList);
            setScrollToItem(isFirstItem ? indexLastItem : indexToReplace);

            break;
          }

          case 'Enter': {
            if (hasIndexSelected) {
              handleSelect(list[indexSelected]);
              setFocus(false);
            }

            break;
          }

          case 'Tab': {
            const defaultIndexSelected =
              list.findIndex((item) => item.id === value?.id) || indexFirstItem;

            if (!(event.target as HTMLInputElement)?.value.length) {
              handleReset();
            } else if (hasIndexSelected) {
              handleSelect(value);
            }

            setScrollToItem(defaultIndexSelected);
            setFocus(false);
            break;
          }
        }
      }
    },
    [focus, handleReset, handleSelect, list, value],
  );

  useEffect(() => {
    if (canDisplayList && fetchList) {
      setError(undefined);
      setLoading(true);

      fetchList(debouncedInput)
        .then((responseList) => {
          handleSelect(value, responseList, true);
        })
        .catch(setError)
        .finally(() => {
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canDisplayList, debouncedInput, fetchList]);

  useEffect(() => {
    setTextInput(typeof value?.label === 'string' ? value.label : '');
  }, [value]);

  return (
    <Box
      className={clsx(classes.wrapper, className)}
      data-testid="auto-complete-container"
      ref={wrapperRef}
      sx={sx}
    >
      <TextField
        className={classes.textField}
        disabled={disabled}
        label={label}
        onChange={handleChange}
        onFocus={() => setFocus(true)}
        onKeyDown={handleKeyDown}
        value={textInput}
        variant={variant}
        {...textFieldProps}
        InputProps={
          startingIcon && {
            startAdornment: (
              <InputAdornment position="start">{startingIcon}</InputAdornment>
            ),
          }
        }
      />

      <div
        onFocus={(e) => {
          e.stopPropagation(); // To prevent Megadraft focus bug
        }}
      >
        {!mandatoryInput && !textInput && (
          <IconButton
            classes={{
              root: clsx(
                variant === 'standard' && classes.arrowIconStandard,
                (variant === 'outlined' || variant === 'filled') &&
                  classes.arrowIconOutlined,
              ),
            }}
            disabled={disabled}
            size="small"
            onClick={() => {
              setFocus((prev) => !prev);
            }}
          >
            {canDisplayList ? <ArrowDropUp /> : <ArrowDropDown />}
          </IconButton>
        )}
        {textInput && (
          <IconButton
            classes={{
              root: clsx(
                variant === 'standard' && classes.crossIconStandard,
                (variant === 'outlined' || variant === 'filled') &&
                  classes.crossIconOutlined,
              ),
            }}
            disabled={disabled}
            onClick={handleReset}
            size="small"
          >
            <Clear fontSize="small" />
          </IconButton>
        )}

        <AutocompleteList
          anchorRef={wrapperRef}
          error={error}
          list={list}
          loading={loading}
          onClose={() => {
            handleSelect(value);
            setFocus(false);
          }}
          onSelect={(item) => {
            handleSelect(item);
            setFocus(false);
          }}
          open={canDisplayList}
          scrollToItem={scrollToItem}
        />
      </div>
    </Box>
  );
};
